import React, { useState, useEffect, useRef } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import { Plus, RefreshCw, AlertTriangle, Database, Clock, History } from "lucide-react";
import useGetQuery from "@/hooks/useGetQuery";
import { clearAuthStorage, getLocalStorageWithExpiry, showSuccess, showApiError } from "@/utils";
import { CLIENT_ID_KEY } from "@/const";
import DataTable from "@/components/dashboard/DataTable";
import BrandButton from "@/components/ui/BrandButton";

const getInvalidUrls = (urls) =>
  urls.filter((line) => {
    try {
      const parsedUrl = new URL(line);
      return !["http:", "https:"].includes(parsedUrl.protocol);
    } catch {
      return true;
    }
  });

const formatDate = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  try {
    let raw = val;
    if (typeof raw === "number" || /^\d+$/.test(String(raw).trim())) {
      const num = Number(raw);
      if (!isNaN(num)) {
        raw = num < 10000000000 ? num * 1000 : num;
      }
    }
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(val);
    const pad = (n) => String(n).padStart(2, "0");
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hours = pad(d.getHours());
    const mins = pad(d.getMinutes());
    return `${year}-${month}-${day} ${hours}:${mins}`;
  } catch {
    return String(val);
  }
};

const renderUrlLink = (val) => {
  if (!val || typeof val !== "string") return val;
  let isSafeScheme = false;
  try {
    const parsed = new URL(val);
    isSafeScheme = ["http:", "https:"].includes(parsed.protocol);
  } catch {
    isSafeScheme = false;
  }

  const clampedLinkStyle = {
    color: "var(--brand-color-dark)",
    textDecoration: "none",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-all",
  };

  const clampedSpanStyle = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-all",
  };

  if (isSafeScheme) {
    return (
      <a
        href={val}
        target="_blank"
        rel="noopener noreferrer"
        className="text-break fw-medium"
        title={val}
        style={clampedLinkStyle}
      >
        {val}
      </a>
    );
  }

  return (
    <span
      className="text-break fw-medium text-secondary"
      title={val}
      style={clampedSpanStyle}
    >
      {val}
    </span>
  );
};

export default function KnowledgeBase() {
  const { t } = useTranslation();
  const AE_BACKEND_URL = (
    process.env.REACT_APP_AE_BACKEND_URL || "/ae-backend"
  ).replace(/\/+$/, "");

  const clientId = getLocalStorageWithExpiry(CLIENT_ID_KEY);
  const userEmail = getLocalStorageWithExpiry("email") || getLocalStorageWithExpiry("username");

  const [subTab, setSubTab] = useState("active");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [urlsInput, setUrlsInput] = useState("");
  const [toRemoveUrl, setToRemoveUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMountedRef = useRef(true);
  const submittingRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const closeAddModal = () => {
    setShowAddModal(false);
    setUrlsInput("");
  };

  const handleCancelAddModal = () => {
    if (isSubmitting || submittingRef.current) return;
    if (urlsInput.trim().length > 0) {
      const confirmDiscard = window.confirm(
        t("answerEngine.knowledgeBase.confirmDiscard", "You have entered unsaved URLs. Are you sure you want to discard them?")
      );
      if (!confirmDiscard) return;
    }
    closeAddModal();
  };

  const closeRemoveModal = () => {
    setShowRemoveModal(false);
    setToRemoveUrl("");
  };

  // Fetch lists using useGetQuery
  const [activeData, activeLoaded, activeError, refetchActive] = useGetQuery(
    clientId ? `${AE_BACKEND_URL}/admin/knowledge-base/${encodeURIComponent(clientId)}/active-urls` : null,
    { staleTime: 30 * 1000 }
  );

  const [pendingData, pendingLoaded, pendingError, refetchPending] = useGetQuery(
    clientId ? `${AE_BACKEND_URL}/admin/knowledge-base/${encodeURIComponent(clientId)}/pending-requests` : null,
    { staleTime: 30 * 1000 }
  );

  const [auditData, auditLoaded, auditError, refetchAudit] = useGetQuery(
    clientId ? `${AE_BACKEND_URL}/admin/knowledge-base/${encodeURIComponent(clientId)}/removal-audit-trail` : null,
    { staleTime: 30 * 1000 }
  );

  const handleAddUrls = async (e) => {
    e.preventDefault();
    if (submittingRef.current || isSubmitting) return;

    if (!clientId || !userEmail) {
      showApiError(t("answerEngine.knowledgeBase.missingContext", "Missing client ID or user email."));
      return;
    }

    const rawLines = urlsInput
      .replace(/\r/g, "")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (rawLines.length === 0) {
      showApiError(t("answerEngine.knowledgeBase.emptyUrls", "Please enter at least one URL."));
      return;
    }

    // Deduplicate URLs in batch
    const lines = Array.from(new Set(rawLines));

    // Limit maximum batch size to 500 URLs
    if (lines.length > 500) {
      showApiError(
        t(
          "answerEngine.knowledgeBase.maxUrlsExceeded",
          "Maximum 500 URLs can be submitted per batch. You provided {{count}} unique URLs.",
          { count: lines.length }
        )
      );
      return;
    }

    const invalidLines = getInvalidUrls(lines);
    if (invalidLines.length > 0) {
      showApiError(
        t("answerEngine.knowledgeBase.invalidUrls", "Some URLs are invalid (must be valid http:// or https:// URLs): {{urls}}", {
          urls: invalidLines.slice(0, 3).join(", ") + (invalidLines.length > 3 ? "..." : ""),
        })
      );
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const resp = await fetch(
        `${AE_BACKEND_URL}/admin/knowledge-base/${encodeURIComponent(clientId)}/add`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            urls: lines,
          }),
        }
      );

      if (!resp.ok) {
        if (resp.status === 401) {
          clearAuthStorage();
          localStorage.setItem("redirectAfterLogin", window.location.pathname + window.location.search);
          window.location.assign("/login");
          return;
        }
        const errorData = await resp.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! Status: ${resp.status}`);
      }

      showSuccess(
        t("answerEngine.knowledgeBase.addSuccess", "Submitted addition requests successfully. Please check the Pending Requests tab to monitor status.")
      );
      closeAddModal();
      
      // Refetch lists
      refetchActive();
      refetchPending();
      setSubTab("pending");
    } catch (err) {
      showApiError(err.message || t("answerEngine.knowledgeBase.addFailed", "Failed to submit addition requests."));
    } finally {
      submittingRef.current = false;
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const handleRemoveUrl = async () => {
    if (submittingRef.current || isSubmitting) return;

    if (!clientId || !userEmail) {
      showApiError(t("answerEngine.knowledgeBase.missingContext", "Missing client ID or user email."));
      return;
    }

    if (!toRemoveUrl) return;

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const resp = await fetch(
        `${AE_BACKEND_URL}/admin/knowledge-base/${encodeURIComponent(clientId)}/remove`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            urls: [toRemoveUrl],
          }),
        }
      );

      if (!resp.ok) {
        if (resp.status === 401) {
          clearAuthStorage();
          localStorage.setItem("redirectAfterLogin", window.location.pathname + window.location.search);
          window.location.assign("/login");
          return;
        }
        const errorData = await resp.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! Status: ${resp.status}`);
      }

      showSuccess(
        t("answerEngine.knowledgeBase.removeSuccess", "Submitted removal request successfully. Please check the Pending Requests tab to monitor status.")
      );
      closeRemoveModal();
      
      // Refetch lists
      refetchActive();
      refetchPending();
      setSubTab("pending");
    } catch (err) {
      showApiError(err.message || t("answerEngine.knowledgeBase.removeFailed", "Failed to submit removal request."));
    } finally {
      submittingRef.current = false;
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  if (!clientId) {
    return (
      <div className="alert alert-warning card-radius shadow-sm p-4 d-flex align-items-center gap-3">
        <AlertTriangle size={24} className="text-warning flex-shrink-0" />
        <div>
          <h5 className="alert-heading fw-bold mb-1">{t("common.error", "Error")}</h5>
          <p className="mb-0">{t("answerEngine.knowledgeBase.noClientId", "No client ID found. Please log in again to manage the knowledge base.")}</p>
        </div>
      </div>
    );
  }

  const inputUrlCount = Array.from(
    new Set(
      urlsInput
        .replace(/\r/g, "")
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
    )
  ).length;

  // Active URLs Columns
  const activeColumns = [
    {
      key: "url",
      header: t("answerEngine.knowledgeBase.columns.url", "URL"),
      render: renderUrlLink,
    },
    {
      key: "added_by",
      header: t("answerEngine.knowledgeBase.columns.addedBy", "Added By"),
      width: "250px",
    },
    {
      key: "added_at",
      header: t("answerEngine.knowledgeBase.columns.addedAt", "Added At"),
      width: "200px",
      render: formatDate,
    },
    {
      key: "actions",
      accessor: "url",
      header: t("answerEngine.knowledgeBase.columns.actions", "Actions"),
      width: "120px",
      render: (urlVal) => (
        <button
          type="button"
          className="btn btn-outline-danger btn-sm rounded-pill px-3"
          onClick={(e) => {
            e.stopPropagation();
            setToRemoveUrl(urlVal);
            setShowRemoveModal(true);
          }}
        >
          {t("common.remove", "Remove")}
        </button>
      ),
    },
  ];

  // Pending Requests Columns
  const pendingColumns = [
    {
      key: "url",
      header: t("answerEngine.knowledgeBase.columns.url", "URL"),
      render: renderUrlLink,
    },
    {
      key: "action",
      header: t("answerEngine.knowledgeBase.columns.action", "Action"),
      width: "140px",
      render: (val) => {
        const isAdd = val === "to_add";
        return (
          <span
            className="badge rounded-pill px-3 py-1 fw-bold d-inline-flex align-items-center gap-1"
            style={
              isAdd
                ? {
                    background: "#e6f7f0",
                    color: "#0d7d4d",
                    border: "1px solid #b7ebd8",
                    fontSize: "12px",
                  }
                : {
                    background: "#fdeded",
                    color: "#c53030",
                    border: "1px solid #f8c4c4",
                    fontSize: "12px",
                  }
            }
          >
            {isAdd ? t("answerEngine.knowledgeBase.actions.add", "Add") : t("answerEngine.knowledgeBase.actions.remove", "Remove")}
          </span>
        );
      },
    },
    {
      key: "requested_by",
      header: t("answerEngine.knowledgeBase.columns.requestedBy", "Requested By"),
      width: "220px",
    },
    {
      key: "requested_at",
      header: t("answerEngine.knowledgeBase.columns.requestedAt", "Requested At"),
      width: "180px",
      render: formatDate,
    },
  ];

  // Removal Audit Columns
  const auditColumns = [
    {
      key: "url",
      header: t("answerEngine.knowledgeBase.columns.url", "URL"),
      render: renderUrlLink,
    },
    {
      key: "removed_by",
      header: t("answerEngine.knowledgeBase.columns.removedBy", "Removed By"),
      width: "250px",
    },
    {
      key: "requested_at",
      header: t("answerEngine.knowledgeBase.columns.requestedAt", "Requested At"),
      width: "200px",
      render: formatDate,
    },
    {
      key: "removed_at",
      header: t("answerEngine.knowledgeBase.columns.removedAt", "Removed At"),
      width: "200px",
      render: formatDate,
    },
  ];

  const safeActiveData = Array.isArray(activeData) ? activeData : [];
  const safePendingData = Array.isArray(pendingData) ? pendingData : [];
  const safeAuditData = Array.isArray(auditData) ? auditData : [];

  const pendingRows = safePendingData.map((row, idx) => ({
    ...row,
    rowKey: `${row.url || ""}::${row.action || ""}::${row.requested_at || ""}::${idx}`,
  }));

  const auditRows = safeAuditData.map((row, idx) => ({
    ...row,
    rowKey: `${row.url || ""}::${row.requested_at || ""}::${row.removed_at || ""}::${idx}`,
  }));

  return (
    <div className="knowledge-base-tab">
      {/* Tab pill selectors */}
      <div className="d-flex align-items-center justify-content-between mb-4 pb-2 border-bottom">
        <div
          role="tablist"
          aria-label={t("answerEngine.sections", "Answer Engine sections")}
          className="btn-group rounded-pill p-1 border shadow-sm"
          style={{ height: "40px", background: "#f3f4f6" }}
        >
          <button
            type="button"
            role="tab"
            aria-selected={subTab === "active"}
            onClick={() => setSubTab("active")}
            className={`btn px-3 d-flex align-items-center gap-2 fw-semibold border-0 btn-sm ${subTab === "active" ? "active" : "text-secondary"}`}
            style={
              subTab === "active"
                ? { background: "#ffffff", color: "var(--brand-navy)", borderRadius: "9999px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
                : { color: "#6b7280" }
            }
          >
            <Database size={15} />
            <span>{t("answerEngine.knowledgeBase.tabs.activeUrls", "Active URLs")}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={subTab === "pending"}
            onClick={() => setSubTab("pending")}
            className={`btn px-3 d-flex align-items-center gap-2 fw-semibold border-0 btn-sm ${subTab === "pending" ? "active" : "text-secondary"}`}
            style={
              subTab === "pending"
                ? { background: "#ffffff", color: "var(--brand-navy)", borderRadius: "9999px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
                : { color: "#6b7280" }
            }
          >
            <Clock size={15} />
            <span>{t("answerEngine.knowledgeBase.tabs.pendingRequests", "Pending Requests")}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={subTab === "audit"}
            onClick={() => setSubTab("audit")}
            className={`btn px-3 d-flex align-items-center gap-2 fw-semibold border-0 btn-sm ${subTab === "audit" ? "active" : "text-secondary"}`}
            style={
              subTab === "audit"
                ? { background: "#ffffff", color: "var(--brand-navy)", borderRadius: "9999px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
                : { color: "#6b7280" }
            }
          >
            <History size={15} />
            <span>{t("answerEngine.knowledgeBase.tabs.removalAudit", "Removal Audit")}</span>
          </button>
        </div>

        {subTab === "active" && (
          <BrandButton onClick={() => setShowAddModal(true)} className="d-flex align-items-center gap-2">
            <Plus size={16} />
            <span>{t("answerEngine.knowledgeBase.addUrls", "Add URLs")}</span>
          </BrandButton>
        )}
      </div>

      {/* Header and Manual Refresh */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="h5 fw-bold text-dark mb-0">
          {subTab === "active"
            ? t("answerEngine.knowledgeBase.activeUrlsTitle", "Active URLs List")
            : subTab === "pending"
              ? t("answerEngine.knowledgeBase.pendingRequestsTitle", "Pending Crawl & Ingestion Requests")
              : t("answerEngine.knowledgeBase.removalAuditTitle", "Removal Audit Trail")}
        </h4>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2 rounded-pill px-3"
          onClick={() => {
            if (subTab === "active") refetchActive();
            else if (subTab === "pending") refetchPending();
            else refetchAudit();
          }}
        >
          <RefreshCw size={13} />
          <span>{t("common.refresh", "Refresh")}</span>
        </button>
      </div>

      {/* Datatable lists based on active subtab */}
      {subTab === "active" && (
        <DataTable
          columns={activeColumns}
          data={safeActiveData}
          idKey="url"
          isLoaded={activeLoaded}
          error={activeError}
          pagination={true}
          emptyStateMessage={t("answerEngine.knowledgeBase.emptyActive", "No active URLs found in the knowledge base.")}
          config={{ cardHeight: "auto" }}
        />
      )}

      {subTab === "pending" && (
        <DataTable
          columns={pendingColumns}
          data={pendingRows}
          idKey="rowKey"
          isLoaded={pendingLoaded}
          error={pendingError}
          pagination={true}
          emptyStateMessage={t("answerEngine.knowledgeBase.emptyPending", "No pending requests at this time.")}
          config={{ cardHeight: "auto" }}
        />
      )}

      {subTab === "audit" && (
        <DataTable
          columns={auditColumns}
          data={auditRows}
          idKey="rowKey"
          isLoaded={auditLoaded}
          error={auditError}
          pagination={true}
          emptyStateMessage={t("answerEngine.knowledgeBase.emptyAudit", "No URL removal history found.")}
          config={{ cardHeight: "auto" }}
        />
      )}

      {/* Add URLs Modal */}
      <Modal
        open={showAddModal}
        onCancel={handleCancelAddModal}
        footer={null}
        centered
        width={720}
        destroyOnHidden
        maskClosable={!isSubmitting}
        closable={!isSubmitting}
        title={(
          <span className="fw-bold text-dark d-flex align-items-center gap-2">
            <Database size={18} style={{ color: "var(--brand-color-dark)" }} />
            {t("answerEngine.knowledgeBase.addModalTitle", "Add Knowledge Base URLs")}
          </span>
        )}
      >
        <form id="knowledge-base-add-form" onSubmit={handleAddUrls}>
          <div className="pt-2">
            <div className="mb-3">
              <label htmlFor="kbUrlsInput" className="form-label fw-bold text-dark">
                {t("answerEngine.knowledgeBase.urlsLabel", "URLs to Add")} <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control font-monospace"
                id="kbUrlsInput"
                rows={8}
                placeholder="https://example.com/page-1&#10;https://example.com/page-2"
                value={urlsInput}
                onChange={(e) => setUrlsInput(e.target.value)}
                required
                disabled={isSubmitting}
                autoFocus
                style={{ fontSize: "13px", resize: "vertical" }}
              />
              <div className="d-flex align-items-center justify-content-between mt-2">
                <div className="form-text text-muted">
                  {t("answerEngine.knowledgeBase.addModalHint", "Enter one URL per line. Addition requests will be processed asynchronously in the background. You can monitor the progress under the 'Pending Requests' tab.")}
                </div>
                {inputUrlCount > 0 && (
                  <span
                    className={`badge ${inputUrlCount > 500 ? "bg-danger" : ""} rounded-pill px-3 py-1 ms-2 flex-shrink-0`}
                    style={
                      inputUrlCount > 500
                        ? { fontSize: "11px" }
                        : { background: "var(--color-bg-1)", color: "var(--brand-navy)", fontSize: "11px" }
                    }
                  >
                    {inputUrlCount} / 500 {t("answerEngine.knowledgeBase.urlsCount", "URLs")}
                  </span>
                )}
              </div>
            </div>

            <div
              className="rounded-3 p-3 mt-3 d-flex align-items-center gap-2"
              style={{ background: "var(--bg-surface)", border: "1px solid #e5e7eb" }}
            >
              <Clock size={16} className="text-secondary" />
              <span className="small text-secondary">
                {t("answerEngine.knowledgeBase.requestedByText", "Requesting as active user: {{email}}", { email: userEmail })}
              </span>
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill px-4"
              onClick={handleCancelAddModal}
              disabled={isSubmitting}
            >
              {t("common.cancel", "Cancel")}
            </button>
            <BrandButton type="submit" disabled={isSubmitting} className="px-4">
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  {t("common.submitting", "Submitting...")}
                </>
              ) : (
                t("answerEngine.knowledgeBase.submitAdd", "Add URLs")
              )}
            </BrandButton>
          </div>
        </form>
      </Modal>

      {/* Remove URL Confirmation Modal */}
      <Modal
        open={showRemoveModal}
        onCancel={closeRemoveModal}
        footer={null}
        centered
        destroyOnHidden
        maskClosable={!isSubmitting}
        closable={!isSubmitting}
        title={(
          <span className="fw-bold text-dark d-flex align-items-center gap-2">
            <AlertTriangle size={18} className="text-danger" />
            {t("answerEngine.knowledgeBase.removeModalTitle", "Confirm URL Removal")}
          </span>
        )}
      >
        <div className="pt-2">
          <p className="text-dark">
            {t("answerEngine.knowledgeBase.confirmRemoveMessage", "Are you sure you want to request removal of this URL from the knowledge base?")}
          </p>
          <div className="p-3 bg-light rounded-3 font-monospace text-break text-danger mb-3" style={{ fontSize: "13px" }}>
            {toRemoveUrl}
          </div>
          <div className="form-text text-muted">
            {t("answerEngine.knowledgeBase.confirmRemoveHint", "This request will be processed in the background. Check the 'Pending Requests' tab to track status.")}
          </div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
          <button
            type="button"
            className="btn btn-outline-secondary rounded-pill px-4"
            onClick={closeRemoveModal}
            disabled={isSubmitting}
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-danger rounded-pill px-4"
            onClick={handleRemoveUrl}
            disabled={isSubmitting}
            autoFocus
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                {t("common.submitting", "Submitting...")}
              </>
            ) : (
              t("common.remove", "Remove")
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
}
